import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { supabase, Nomination } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useCategories } from '../../hooks/useCategories';
import NominationDetails from '../nominations/NominationDetails';
import './Dashboard.css';
import '../landing/Landing.css';

const PublicDashboard: React.FC = () => {
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [selectedNomination, setSelectedNomination] = useState<Nomination | null>(null);
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [submissionEnabled, setSubmissionEnabled] = useState(true);
  const [votingEnabled, setVotingEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const { user } = useAuth();
  const { categories } = useCategories();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadMyNominations();
      loadEditingConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMyNominations = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .schema('premios')
        .from('nominations')
        .select('*')
        .eq('submitter_email', user?.email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNominations(data || []);
    } catch (error) {
      console.error('Error loading nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEditingConfig = async () => {
    try {
      const { data, error } = await supabase
        .schema('premios')
        .from('platform_config')
        .select('nominations_editing_enabled, nominations_submission_enabled, public_voting_enabled')
        .single();

      if (error) throw error;
      setEditingEnabled(data?.nominations_editing_enabled ?? false);
      setSubmissionEnabled(data?.nominations_submission_enabled ?? true);
      setVotingEnabled(!!data?.public_voting_enabled);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const getCategoryName = (categoryId: string) => {
    return categories.find((c) => c.id === categoryId)?.name || 'Categoria desconocida';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-pending">Pendiente</span>;
      case 'approved':
        return <span className="badge badge-approved">Presentada</span>;
      case 'rejected':
        return <span className="badge badge-rejected">Rechazada</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container dashboard">
      {votingEnabled && (
        <div
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 24,
            border: '1.5px solid var(--color-secondary)',
            background: 'rgba(255, 199, 0, 0.06)',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>¡La votación pública ya está abierta!</h2>
            <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)' }}>
              Elige a tus finalistas favoritos/as en cada categoría.
            </p>
          </div>
          <Link to="/votar" className="btn btn-primary" style={{ width: 'auto', flexShrink: 0 }}>
            Ir a votar
          </Link>
        </div>
      )}

      <div className="dashboard-header" style={{ position: 'relative' }}>
        <h1 style={{ margin: 0 }}>Mis Candidaturas</h1>
        <p style={{ margin: '4px 0 0' }}>Premios NovaGob Excelencia {new Date().getFullYear()}</p>
        {submissionEnabled && nominations.length > 0 && (
          <button
            className="btn btn-primary"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              fontSize: '13px',
              padding: '6px 14px',
              width: 'auto',
            }}
            onClick={() => navigate('/nominate')}
          >
            + Presentar nueva candidatura
          </button>
        )}
      </div>

      {!submissionEnabled && nominations.length > 0 && (
        <div
          className="alert alert-warning"
          style={{ marginTop: 16 }}
        >
          <strong>Presentación de candidaturas:</strong> en breve se comunicará la apertura de la próxima convocatoria. Mientras tanto, puedes seguir consultando las candidaturas que ya has presentado.
        </div>
      )}

      {nominations.length === 0 ? (
        <div className="finaltwo-slide-card" style={{ maxWidth: 560, margin: '32px auto 0', textAlign: 'center' }}>
          <span className="finaltwo-hero-kicker" style={{ marginBottom: 18 }}>
            <Sparkles size={14} /> {submissionEnabled ? 'Presentación abierta' : 'Presentación cerrada'}
          </span>
          <h1 className="finaltwo-hero-title" style={{ fontSize: 'clamp(22px, 3.2vw, 30px)' }}>
            No has presentado candidaturas
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', margin: '14px 0 28px', fontSize: 15 }}>
            {submissionEnabled
              ? 'Haz clic en el botón para presentar tu primera candidatura.'
              : 'En breve se comunicará la apertura de la próxima convocatoria. Vuelve más adelante para presentar tu candidatura.'}
          </p>
          {submissionEnabled && (
            <button type="button" className="landing-btn landing-btn-primary" onClick={() => navigate('/nominate')}>
              + Presentar nueva candidatura
            </button>
          )}
        </div>
      ) : (
        <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {nominations.map((nom) => (
            <div key={nom.id} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <h3 style={{ margin: 0 }}>{nom.candidate_name || nom.project_title}</h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                    {getCategoryName(nom.category_id)} &middot; {new Date(nom.created_at).toLocaleDateString('es-ES')}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {getStatusBadge(nom.status)}
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '13px', padding: '6px 14px' }}
                    onClick={() => setSelectedNomination(nom)}
                  >
                    Ver detalles
                  </button>
                  {editingEnabled && nom.status === 'pending' && (
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '13px', padding: '6px 14px' }}
                      onClick={() => navigate(`/nominate/${nom.id}`)}
                    >
                      Editar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedNomination && (
        <div className="modal-overlay" onClick={() => setSelectedNomination(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <NominationDetails
              nomination={selectedNomination}
              category={categories.find((c) => c.id === selectedNomination.category_id)}
              onClose={() => setSelectedNomination(null)}
              showActions={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicDashboard;
